/**
 * Shared gate helper for buyer claim/reserve actions. Mirrors the backend role
 * guard in the buyer-claim-split resolver:
 *   blocked = tenant != null || (role != null && role !== 'basic')
 * (every producer/processor/admin has a tenantId; buyers do not). A role-unset,
 * tenant-null session is a genuine legacy buyer and is allowed.
 */

export type ClaimGateDecision = 'anonymous' | 'producer' | 'allowed';

/** Minimal shape consumed by claimGateDecision — compatible with CognitoUser. */
export interface ClaimGateUser {
  tenantId?: string | null;
  role?: string | null;
}

/**
 * Determines what a user can do at a claim/reserve trigger.
 * @param isAuthenticated
 * @param user
 * @returns
 *   'anonymous' — not logged in → redirect to /login
 *   'producer'  — authenticated but blocked (has tenantId or non-basic role)
 *   'allowed'   — basic buyer (or role-unset + tenant-null legacy buyer)
 */
export function claimGateDecision(
  isAuthenticated: boolean,
  user: ClaimGateUser | null | undefined,
): ClaimGateDecision {
  if (!isAuthenticated) return 'anonymous';

  // Mirror backend: blocked = tenant != null || (role != null && role !== 'basic')
  const isBlocked =
    user?.tenantId != null || (user?.role != null && user.role !== 'basic');

  return isBlocked ? 'producer' : 'allowed';
}

/**
 * Shown to a producer/processor who tries to claim a marketplace split.
 * Used by both claim sites (available.tsx, animal.tsx, processor.tsx) and as
 * the fallback copy when the backend PRODUCER_ACCOUNT_CANNOT_CLAIM error fires.
 */
export const PRODUCER_CLAIM_BLOCKED_MSG =
  "This is already a producer/processor account — buying from a producer account isn't available yet.";

/**
 * Shown on the register screen when ACCOUNT_IS_PRODUCER fires — the email
 * belongs to an existing producer/processor/admin account.
 */
export const REGISTRATION_COLLISION_MSG =
  'This email is already registered as a producer or processor account.';
